import {
  getCurrentFacilityId,
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
  userLoggedInFacilityType,
} from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ToggleSwitch } from "risa-oasis-ui_v2";
import DynamicSingleSideTab from "../../components/dynamicSingleSideTab/dynamicSingleSideTab";
import { RootState } from "../../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../store/UserGeneralInfoStore";
import ChevronDown from "../../svg/chevron-down";
import { getEnv } from "../../utils/runtimeEnv";

const Settings = () => {
  const [sideNavElements, setSideNavElements] = useState<
    { id: string; label: string }[]
  >([
    { id: "profile", label: "Profile" },
    { id: "security", label: "Security" },
  ]);

  const [viewLegacy, setViewLegacy] = useState(false);
  const [showNavigation, setShowNavigation] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");

  const location = useLocation();

  const { data: provider } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const navigate = useNavigate();

  // Build navigation elements based on user role
  useEffect(() => {
    const buildNavElements = () => {
      const navElements = [
        { id: "profile", label: "Profile" },
        { id: "security", label: "Security" },
      ];

      if (isCurrentUserSuperAdmin() || isCurrentUserAdmin()) {
        navElements.push({ id: "members", label: "Members" });
      }

      if (isCurrentUserSuperAdmin()) {
        navElements.push({ id: "superadmin", label: "Super Admin" });
      }

      setSideNavElements(navElements);
    };

    // Run immediately
    buildNavElements();
  }, [provider]);

  useEffect(() => {
    const storedViewLegacy = UserStore.getItem(
      UserGeneralInfoContextKeys.VIEW_LEGACY,
    );
    setViewLegacy(storedViewLegacy !== null ? storedViewLegacy : false);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    if (currentPath) {
      setActiveTab(currentPath);
    }
  }, [location.pathname]);

  const handleViewLegacyChange = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    UserStore.setItem(UserGeneralInfoContextKeys.VIEW_LEGACY, data.value);
    navigate("/");
    window.location.reload();
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    navigate(tabId);
  };

  const currentFacilityId = getCurrentFacilityId() || "";
  const isExternalFacility =
    userLoggedInFacilityType(currentFacilityId) === "external";

  return (
    <div className="settings--layout">
      {/* Sidebar */}
      <div
        className={`flex h-full ${
          showNavigation ? "settings--navigation" : "settings--navigation__hide"
        }`}
      >
        <div
          className={`settings--navigation__container flex flex-col overflow-hidden ${
            showNavigation ? "animate-slide-in w-full" : "animate-slide-out w-0"
          }`}
        >
          {/* Title */}
          <div
            className={`settings--navigation__header my-3 w-full truncate pl-2 text-lg font-bold leading-normal text-black ${
              showNavigation ? "animate-fade-up" : "animate-fade-up-out"
            }`}
          >
            Settings
          </div>

          {/* Links */}
          <div
            className={`settings--navigation__body flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden ${
              showNavigation ? "animate-fade-down" : "animate-fade-down-out"
            }`}
          >
            {sideNavElements.map((sideNavElement, index) => (
              <div
                key={sideNavElement.id}
                className={`${
                  showNavigation ? "animate-stagger-in" : "animate-stagger-out"
                }`}
                style={{ transitionDelay: `${10 + index * 50}ms` }}
              >
                <DynamicSingleSideTab
                  id={sideNavElement.id}
                  label={sideNavElement.label}
                  isActive={activeTab === sideNavElement.id}
                  onTabClick={handleTabClick}
                />
              </div>
            ))}
          </div>

          {/* Legacy Toggle */}
          {isExternalFacility && (
            <div className="mt-auto border-t border-primaryGray-15 pt-3">
              <div className="flex flex-row items-center justify-start gap-2 px-2">
                <div className="text-sm text-primaryGray-11">
                  View Legacy Version
                </div>
                <ToggleSwitch
                  key={viewLegacy ? "legacy-on" : "legacy-off"}
                  id="view-legacy"
                  defaultChecked={viewLegacy}
                  onChangeEmit={handleViewLegacyChange}
                />
              </div>
            </div>
          )}

          {/* Version Number */}
          <div
            className={`px-2 pb-3 ${isExternalFacility ? "pt-2" : "mt-auto pt-3"}`}
          >
            <div className="text-xs text-primaryGray-11/60">
              {getEnv("REACT_APP_VERSION")
                ? `v${getEnv("REACT_APP_VERSION")}`
                : ""}
            </div>
          </div>
        </div>

        {/* Toggle Icon */}
        <div className="settings--navigation__icon relative border border-primaryGray-15">
          <div
            className="left-arrow--icon absolute items-center justify-center rounded-full border bg-white p-1 transition-transform duration-300 ease-in-out hover:scale-105 hover:cursor-pointer"
            style={{
              filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))",
              left: "-14px",
              top: "22px",
            }}
            onClick={() => setShowNavigation(!showNavigation)}
          >
            <ChevronDown rotate={!showNavigation ? 270 : 90} />
          </div>
        </div>
      </div>

      {/* Main Outlet */}
      <div
        className={`settings--outlet px-3 pt-6 ${
          !showNavigation ? "settings--outlet__hide-left" : ""
        }`}
      >
        <Outlet />
      </div>
    </div>
  );
};

export default Settings;
